import type { Breadcrumb, Event } from "@sentry/react";

/**
 * Features que carregam recursos auxiliares (campanhas, banner, RSS). Quando a
 * rede do usuário falha neles o app segue com o fallback; não é bug nosso e
 * consumia a cota do plano gratuito do Sentry.
 */
const AUXILIARY_FEATURES = ["alt-redirect", "rss", "inline-banner"];

/**
 * Mensagens que o navegador dá quando o `fetch` nem chega a ter resposta, com o
 * sufixo ` (host)` que o SDK acrescenta. Ancorada no fim para não pegar erros
 * nossos que começam igual, como `Failed to fetch RSS feed: 503`.
 */
const FETCH_NETWORK_FAILURE =
  /^(Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.?)( \([^)]+\))?$/;

/** Descarta falha de rede em recurso auxiliar. Erros de conteúdo (HTTP 403, JSON inválido) continuam sendo reportados. */
export const shouldDropEvent = (event: Event): boolean => {
  const feature = event.tags?.feature;
  const exception = event.exception?.values?.[0];
  const message = exception?.value ?? "";

  return (
    exception?.type === "TypeError" &&
    typeof feature === "string" &&
    AUXILIARY_FEATURES.includes(feature) &&
    FETCH_NETWORK_FAILURE.test(message)
  );
};

/** O progresso do polling da SERP é o único `console.log` útil num evento. */
const POLLING_PROGRESS =
  /^(Enviando Request|Recebendo Response|Busca Finalizada) /;

const VERBOSE_CONSOLE_LEVELS = ["log", "info", "debug"];

/**
 * Os `console.log` de depuração da SERP viravam breadcrumbs com objetos como
 * `[object Object]`, sem informação, e ainda engordavam o evento (houve
 * `too_large:event`). Ficam `warn`/`error` e o progresso do polling.
 */
export const filterBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
  if (
    breadcrumb.category === "console" &&
    VERBOSE_CONSOLE_LEVELS.includes(breadcrumb.level ?? "log") &&
    !POLLING_PROGRESS.test(breadcrumb.message ?? "")
  ) {
    return null;
  }

  return breadcrumb;
};
